import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CustomAutoComplete,
  InfoTooltip,
  MaterialQuantityTable,
  renderTwoLineOption,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { useUnits } from '../../../hooks/useUnits';
import { useInventoryArticleSearch } from '../../internal-orders/hooks/useInventoryArticleSearch';
import { SUPPLY_ORDER_MATERIAL_TEXTS } from '../constants/supplyOrdersConstants';

/**
 * Renglones del borrador con buscador para agregar artículos.
 * Sin búsqueda habilitada (compra consolidada) solo se editan o quitan los renglones técnicos.
 * @param {Object} props
 * @param {Array<Object>} props.materials Renglones del formulario.
 * @param {boolean} [props.searchEnabled] Si se ofrece el buscador para agregar artículos.
 * @returns {JSX.Element}
 */
const SupplyOrderMaterialsSection = ({
  materials = [],
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
}) => {
  const { data: units = [] } = useUnits({ enabled: searchEnabled });
  const {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching,
    isPendingDebounce,
    hasSearchError,
    resetSearch,
    minSearchLength,
  } = useInventoryArticleSearch(units, searchEnabled);

  const addedMaterialIds = useMemo(
    () => new Set(materials.map((item) => item.materialId)),
    [materials]
  );

  const availableOptions = useMemo(() => {
    if (isPendingDebounce) return [];
    return materialOptions.filter((option) => !addedMaterialIds.has(option.value));
  }, [materialOptions, addedMaterialIds, isPendingDebounce]);

  const handleMaterialSelect = (_, option) => {
    if (!option) return;
    onAddMaterial(option);
    resetSearch();
  };

  const hasTechnicalLines = materials.some((material) => material.uuid_external_order_item);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {SUPPLY_ORDER_MATERIAL_TEXTS.title}
        </Typography>
        {!searchEnabled && hasTechnicalLines && (
          <InfoTooltip lines={[SUPPLY_ORDER_MATERIAL_TEXTS.consolidatedOnly]} />
        )}
      </Box>

      {searchEnabled && (
        <CustomAutoComplete
          label={SUPPLY_ORDER_MATERIAL_TEXTS.search}
          inputPlaceholder={SUPPLY_ORDER_MATERIAL_TEXTS.searchPlaceholder}
          options={availableOptions}
          value={null}
          inputValue={searchTerm}
          onInputChange={(_, value, reason) => {
            if (reason !== 'reset') setSearchTerm(value);
          }}
          onChange={handleMaterialSelect}
          loading={isSearching}
          filterOptions={(options) => options}
          renderOption={renderTwoLineOption}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          noOptionsText={
            hasSearchError
              ? SUPPLY_ORDER_MATERIAL_TEXTS.searchError
              : isPendingDebounce
                ? SUPPLY_ORDER_MATERIAL_TEXTS.searching
                : searchTerm.trim().length >= minSearchLength
                  ? SUPPLY_ORDER_MATERIAL_TEXTS.noResults
                  : SUPPLY_ORDER_MATERIAL_TEXTS.minLength(minSearchLength)
          }
          sx={{ mt: 0 }}
        />
      )}

      <MaterialQuantityTable
        rows={materials.map((material) => ({
          key: material.lineId || material.materialId,
          code: material.code,
          name: material.name,
          detail: material.origin
            ? `${material.origin.order_number} · ${material.origin.label}`
            : undefined,
          unit: material.unit,
          quantity: material.quantity,
        }))}
        onQuantityChange={onUpdateMaterialQuantity}
        onRemove={onRemoveMaterial}
      />
    </Box>
  );
};

export default SupplyOrderMaterialsSection;
